import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, User, Building, Phone, Eye, EyeOff, LogIn, UserPlus } from 'lucide-react';

// Staff land on /staff by default; installers / public users land on the
// installer-style home grid. An explicit `?redirect=` always wins.
const landingFor = (u, fallbackStaff) => {
  if (u?.role === 'affiliate') return '/affiliate';
  if (u?.role && ['admin', 'manager', 'team'].includes(u.role)) return '/staff';
  return fallbackStaff ? '/staff' : '/';
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, loginWithGoogle, isAuthenticated, loading, isStaff, user } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: ''
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      const redirect = searchParams.get('redirect') || landingFor(user, isStaff);
      navigate(redirect);
    }
  }, [isAuthenticated, loading, navigate, searchParams, user, isStaff]);

  // Check for register param
  useEffect(() => {
    if (searchParams.get('register') === 'true') {
      setMode('register');
    }
  }, [searchParams]);

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (sessionId) => {
    setIsSubmitting(true);
    setError('');
    try {
      await loginWithGoogle(sessionId);
      const redirect = searchParams.get('redirect') || landingFor(user, isStaff);
      navigate(redirect);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [loginWithGoogle, navigate, searchParams, user, isStaff]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      handleGoogleCallback(sessionId);
    }
  }, [searchParams, handleGoogleCallback]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      let signedIn;
      if (mode === 'login') {
        signedIn = await login(formData.email, formData.password);
      } else {
        signedIn = await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          company_name: formData.company_name || null,
          phone: formData.phone || null
        });
      }
      const redirect = searchParams.get('redirect') || landingFor(signedIn, false);
      navigate(redirect);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google OAuth not yet configured. To enable, swap this for your own provider.
    alert('Google sign-in is not yet enabled — please use email & password to log in.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2F5E96]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECEF] flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-4 border-[#2F5E96]">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-4 md:gap-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg" 
              alt="Ox Resin ProBound" 
              className="h-10 md:h-14 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/ta6bz3y6_IMG_8292.jpeg" 
              alt="Ox Resin ProEpoxy" 
              className="h-10 md:h-14 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/4z707ot7_IMG_8289.jpeg" 
              alt="Ox Resin ProDecor" 
              className="h-10 md:h-14 cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-[#27292E]">
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h1>
              <p className="text-gray-500 mt-2">
                {mode === 'login' 
                  ? 'Sign in to access your installer portal' 
                  : 'Register to get started with Ox Resin'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Google Login Button — hidden until Google OAuth is configured
            <Button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isSubmitting}
              variant="outline"
              className="w-full h-12 mb-6 border-2 border-gray-200 hover:bg-gray-50"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or</span>
              </div>
            </div>
            */}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <>
                  {/* Registration Notice */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 text-sm mb-4">
                    <p className="font-medium">Note: After registering, Ox Resin staff will need to grant you access to the relevant sections based on your training.</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="pl-10 h-12 border-2 rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Company Name
                    </Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Smith Driveways Ltd"
                        className="pl-10 h-12 border-2 rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Phone Number
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="07700 900000"
                        className="pl-10 h-12 border-2 rounded-xl"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    className="pl-10 h-12 border-2 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="pl-10 pr-10 h-12 border-2 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {mode === 'login' && (
                  <div className="text-right mt-1.5">
                    <button
                      type="button"
                      data-testid="login-forgot-link"
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-[#7A0B7A] hover:underline font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-[#300122] hover:bg-[#4a0636] text-white font-bold rounded-xl mt-6"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                    {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {mode === 'login' ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                    {mode === 'login' ? 'Sign In' : 'Create Account'}
                  </span>
                )}
              </Button>
            </form>

            {/* Toggle Mode */}
            <div className="mt-6 text-center">
              <p className="text-gray-500">
                {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
                <button
                  type="button"
                  onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                  className="ml-2 text-[#2F5E96] font-semibold hover:underline"
                >
                  {mode === 'login' ? 'Register' : 'Sign In'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
